import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import LoginIcon from '@mui/icons-material/Login';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';

const CHUNK_ERROR_RETRY_KEY = 'LANCEQ_CHUNK_ERROR_RETRIED';

const getErrorMessage = (error: unknown): string => {
  if (isRouteErrorResponse(error)) {
    return error.statusText || error.data?.message || `Route error ${error.status}`;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return String(error || '');
};

const isDynamicImportError = (message: string): boolean =>
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError/i.test(
    message,
  );

const clearAppCaches = async () => {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.update().catch(() => undefined)));
  }

  if ('caches' in window) {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
  }
};

const AppRouteErrorFallback = ({ error }: { error?: unknown }) => {
  const routeError = useRouteError();
  const activeError = error ?? routeError;
  const message = getErrorMessage(activeError);
  const isChunkError = useMemo(() => isDynamicImportError(message), [message]);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (!isChunkError) return;

    const alreadyRetried = sessionStorage.getItem(CHUNK_ERROR_RETRY_KEY) === 'true';
    if (alreadyRetried) return;

    sessionStorage.setItem(CHUNK_ERROR_RETRY_KEY, 'true');
    window.location.reload();
  }, [isChunkError]);

  const refreshApp = async () => {
    setIsRefreshing(true);
    try {
      await clearAppCaches();
    } finally {
      window.location.reload();
    }
  };

  const goToLogin = async () => {
    setIsRefreshing(true);
    try {
      await clearAppCaches();
    } finally {
      window.location.replace('/freelancer/auth/login');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f8fafc',
        p: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 520,
          p: { xs: 3, sm: 4 },
          border: '1px solid #e5e7eb',
          borderRadius: 3,
          textAlign: 'center',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827', mb: 1 }}>
          {isChunkError ? 'App update required' : 'Something went wrong'}
        </Typography>
        <Typography variant="body1" sx={{ color: '#4b5563', mb: 3 }}>
          {isChunkError
            ? 'We could not load the latest app files. This usually happens after a deployment or when an old session resumes.'
            : 'The page could not be loaded. Please refresh and try again.'}
        </Typography>

        <Alert severity={isChunkError ? 'info' : 'error'} sx={{ mb: 3, textAlign: 'left' }}>
          {isChunkError
            ? 'Refresh the app to load the newest version, then sign in again if your session expired.'
            : message || 'Unknown application error'}
        </Alert>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<RefreshIcon />}
            disabled={isRefreshing}
            onClick={refreshApp}
            sx={{ textTransform: 'none', bgcolor: '#199ecf', '&:hover': { bgcolor: '#127da6' } }}
          >
            Refresh App
          </Button>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<LoginIcon />}
            disabled={isRefreshing}
            onClick={goToLogin}
            sx={{ textTransform: 'none' }}
          >
            Go to Login
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default AppRouteErrorFallback;
